import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { formatUSD } from "../lib/api";

const FALLBACK_PRODUCTS = [
  { product_id: "bar-500g", name: "500 g Cast Bullion Bar", weight_grams: 500, price_usd: 60000, finish: "Cast", purity: "999.9 fine gold", image: "/photos/bars-case.jpg" },
  { product_id: "bar-1kg", name: "1 kg Cast Bullion Bar", weight_grams: 1000, price_usd: 120000, finish: "Cast", purity: "999.9 fine gold", image: "/photos/bar-palm.jpg" },
  { product_id: "bar-2500g", name: "2.5 kg Cast Bullion Bar", weight_grams: 2500, price_usd: 300000, finish: "Cast", purity: "999.9 fine gold", image: "/photos/bar-fingers.jpg" },
  { product_id: "bar-5kg", name: "5 kg Cast Bullion Bar", weight_grams: 5000, price_usd: 600000, finish: "Cast", purity: "999.9 fine gold", image: "/photos/bars-case.jpg" },
  { product_id: "bar-10kg", name: "10 kg Cast Bullion Bar", weight_grams: 10000, price_usd: 1200000, finish: "Cast", purity: "999.9 fine gold", image: "/photos/nuggets-hand.jpg" },
  { product_id: "bar-12500g", name: "12.5 kg Good Delivery Bar", weight_grams: 12500, price_usd: 1500000, finish: "Good Delivery", purity: "999.9 fine gold", image: "/photos/bar-fingers.jpg" },
];

const ProductCard = ({ product, onQuote, onSpecs, index }) => (
  <motion.article
    data-testid={`product-card-${product.weight_grams}g`}
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: (index % 3) * 0.1 }}
    className="group h-full flex flex-col bg-white border border-[#E6E0D4] hover:border-[#C5A059]/70 hover:shadow-[0_10px_30px_-10px_rgba(20,19,18,0.08),0_0_1px_1px_rgba(197,160,89,0.15)] transition-all duration-500 hover:-translate-y-1"
  >
    <div className="relative aspect-[4/3] overflow-hidden bg-[#141312]">
      <img
        src={product.image}
        alt={product.name}
        loading="lazy"
        className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
      />
      <div className="absolute inset-0 spotlight-hover" aria-hidden="true" />
      <span className="absolute top-4 left-4 bg-[#FAF8F5]/95 px-3 py-1.5 font-mono text-[11px] tracking-[0.18em] text-[#1A1816]">
        {product.weight_grams >= 1000 ? `${product.weight_grams / 1000} KG` : `${product.weight_grams} G`}
      </span>
      <span className="absolute top-4 right-4 border border-[#D4AF37]/60 px-2.5 py-1.5 font-mono text-[10px] tracking-[0.2em] text-[#D4AF37] bg-[#141312]/70">
        999.9
      </span>
    </div>
    <div className="flex flex-col flex-1 p-6">
      <p className="text-[10px] tracking-[0.28em] uppercase text-[#8C6D27]">{product.finish} · Assay-certified</p>
      <h3 className="font-serif text-2xl text-[#1A1816] mt-2 leading-tight">{product.name}</h3>
      <p className="font-mono text-lg text-[#1A1816] mt-3">{formatUSD(product.price_usd)}</p>
      <p className="font-mono text-xs text-[#6E6A63] mt-1">
        {formatUSD(product.price_usd / (product.weight_grams / 1000))}/kg — serial-numbered
      </p>
      <div className="mt-6 pt-5 border-t border-[#E6E0D4] flex gap-3 mt-auto">
        <Button
          data-testid={`product-quote-btn-${product.weight_grams}g`}
          onClick={() => onQuote(product)}
          className="flex-1 rounded-none h-11 bg-[#141312] text-[#FAF8F5] hover:bg-[#8C6D27] text-[12px] tracking-[0.14em] uppercase transition-colors duration-500"
        >
          Request Quote
        </Button>
        <Button
          data-testid={`product-specs-btn-${product.weight_grams}g`}
          onClick={() => onSpecs(product)}
          variant="outline"
          className="rounded-none h-11 px-5 border-[#E6E0D4] text-[#1A1816] hover:border-[#C5A059] hover:bg-[#C5A059]/10 text-[12px] tracking-[0.14em] uppercase transition-colors duration-500"
        >
          Specs
        </Button>
      </div>
    </div>
  </motion.article>
);

const ProductCatalog = ({ products, onQuote }) => {
  const list = products?.length ? products : FALLBACK_PRODUCTS;
  const [specProduct, setSpecProduct] = useState(null);

  return (
    <section id="catalog" className="py-24 lg:py-32 bg-[#FAF8F5]">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-wrap items-end justify-between gap-6 mb-14"
        >
          <div>
            <p className="text-xs font-medium tracking-[0.3em] uppercase text-[#8C6D27] mb-4">Bullion Catalog</p>
            <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1816]">
              Wholesale bars, <em className="gold-foil-text">mine-direct.</em>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-[#6E6A63] font-light">
            Every bar is refined, serial-numbered and assay-certified at our Monrovia headquarters.
            Prices start at <span className="font-mono text-[#1A1816]">$60,000</span> for the 500 g bar.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((product, i) => (
            <ProductCard
              key={product.product_id}
              product={product}
              index={i}
              onQuote={onQuote}
              onSpecs={setSpecProduct}
            />
          ))}
        </div>
      </div>

      <Dialog open={!!specProduct} onOpenChange={(open) => !open && setSpecProduct(null)}>
        <DialogContent className="rounded-none bg-[#FAF8F5] border border-[#E6E0D4] max-w-md" data-testid="product-specs-dialog">
          {specProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#1A1816]">{specProduct.name}</DialogTitle>
                <DialogDescription className="text-xs tracking-[0.2em] uppercase text-[#8C6D27]">
                  Specification sheet
                </DialogDescription>
              </DialogHeader>
              <dl className="mt-4 divide-y divide-[#E6E0D4] text-sm">
                {[
                  ["Weight", `${specProduct.weight_grams >= 1000 ? (specProduct.weight_grams / 1000) + " kg" : specProduct.weight_grams + " g"}`],
                  ["Purity", "999.9 fine gold (24 karat)"],
                  ["Finish", `${specProduct.finish} · serial-numbered`],
                  ["Origin", "Nimba Range mines, Liberia"],
                  ["Certification", "Assay-certified at Monrovia HQ"],
                  ["Wholesale price", formatUSD(specProduct.price_usd)],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between py-3">
                    <dt className="text-[#6E6A63]">{k}</dt>
                    <dd className="font-mono text-[#1A1816] text-right">{v}</dd>
                  </div>
                ))}
              </dl>
              <Button
                data-testid="specs-dialog-quote-btn"
                onClick={() => {
                  setSpecProduct(null);
                  onQuote(specProduct);
                }}
                className="mt-6 w-full rounded-none h-12 bg-[#141312] text-[#FAF8F5] hover:bg-[#8C6D27] text-[12px] tracking-[0.14em] uppercase"
              >
                Request Quote — {formatUSD(specProduct.price_usd)}
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ProductCatalog;
